import React, { useState } from 'react';
import { MessageSquare, BarChart2, Wrench, Upload, X, CheckCircle, AlertCircle } from 'lucide-react';

const WidgetEditor = ({
  tempPrompt,
  setTempPrompt,
  tempChartPrompt,
  setTempChartPrompt,
  tool,
  onRagUpload,
}) => {
  const [ragFile, setRagFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null); // 'success' | 'error' | null

  const handleUpload = async () => {
    if (!ragFile || !onRagUpload) return;
    setUploading(true);
    setUploadStatus(null);
    try {
      await onRagUpload(ragFile);
      setUploadStatus('success');
      setRagFile(null);
    } catch {
      setUploadStatus('error');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-3 mt-1">
      {tool && (
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wider">
            <Wrench size={11} />
            Tool
          </label>
          <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded text-sm text-gray-600 font-medium">
            {tool}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wider">
          <MessageSquare size={11} />
          Prompt
        </label>
        <textarea
          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 placeholder-gray-300 resize-none h-[18vh] focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
          value={tempPrompt}
          onChange={e => setTempPrompt(e.target.value)}
          placeholder="Enter the main prompt for this widget…"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wider">
          <BarChart2 size={11} />
          Visualization prompt
          <span className="normal-case font-normal text-gray-300 tracking-normal ml-0.5">— optional</span>
        </label>
        <input
          className="w-full px-3 py-2.5 bg-gray-50 border border-gray-200 rounded text-sm text-gray-700 placeholder-gray-300 focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
          type="text"
          value={tempChartPrompt || ''}
          onChange={e => setTempChartPrompt(e.target.value)}
          placeholder="e.g. Give me a pie chart"
        />
      </div>

      {tool === 'Retrieval Tool' && (
        <div className="flex flex-col gap-1.5">
          <label className="flex items-center gap-1.5 text-xs font-medium text-gray-400 uppercase tracking-wider">
            <Upload size={11} />
            Upload Document
          </label>
          <div className="border-2 border-dashed border-gray-200 rounded bg-gray-50">
            <input
              type="file"
              id="rag-editor-file"
              accept=".pdf,.txt,.docx,.doc,.csv"
              className="hidden"
              onChange={e => {
                setRagFile(e.target.files[0] || null);
                setUploadStatus(null);
              }}
            />
            {ragFile ? (
              <div className="flex items-center justify-between px-3 py-2.5">
                <span className="text-sm text-gray-700 truncate">{ragFile.name}</span>
                <button
                  type="button"
                  onClick={() => { setRagFile(null); setUploadStatus(null); }}
                  className="ml-2 text-gray-400 hover:text-gray-600 shrink-0"
                >
                  <X size={13} />
                </button>
              </div>
            ) : (
              <label
                htmlFor="rag-editor-file"
                className="flex flex-col items-center gap-1 py-3 cursor-pointer"
              >
                <Upload size={16} className="text-gray-400" />
                <span className="text-xs text-gray-500">Click to upload a new document</span>
                <span className="text-xs text-gray-400">PDF, TXT, DOCX, CSV</span>
              </label>
            )}
          </div>

          {ragFile && (
            <button
              type="button"
              onClick={handleUpload}
              disabled={uploading}
              className="self-start px-3 py-1.5 text-xs font-medium rounded bg-primary text-white hover:opacity-90 disabled:opacity-50 transition-all"
            >
              {uploading ? 'Uploading…' : 'Upload to collection'}
            </button>
          )}

          {uploadStatus === 'success' && (
            <div className="flex items-center gap-1.5 text-xs text-green-600">
              <CheckCircle size={12} />
              Document uploaded successfully
            </div>
          )}
          {uploadStatus === 'error' && (
            <div className="flex items-center gap-1.5 text-xs text-red-500">
              <AlertCircle size={12} />
              Upload failed. Please try again.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WidgetEditor;
